import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

import { SupplierQuickSelect } from './SupplierQuickSelect';
import { InvoicePhotoCapture } from './InvoicePhotoCapture';
import { DocumentDropzone } from './DocumentDropzone';
import { GoodsPhotoGrid } from './GoodsPhotoGrid';
import { OrderSummaryCard } from './OrderSummaryCard';

interface OrderCheckInWizardProps {
  onBack: () => void;
  onSubmit: (orderData: any) => void;
}

type WizardStep = 'basics' | 'invoice' | 'documents' | 'goods' | 'review';

interface OrderData {
  supplierCode: string;
  deliveryDate: string;
  invoicePhotos: string[];
  otherDocuments: string[];
  goodsPhotos: string[];
}

const steps: { key: WizardStep; title: string; description: string }[] = [
  { key: 'basics', title: 'Order Basics', description: 'Supplier & delivery date' },
  { key: 'invoice', title: 'Invoice Photos', description: 'Capture invoice pages (Optional)' },
  { key: 'documents', title: 'Other Documents', description: 'Upload additional docs (Optional)' },
  { key: 'goods', title: 'Goods Photos', description: 'Capture product photos (Optional)' },
  { key: 'review', title: 'Review & Create', description: 'Final review' }
];

export const OrderCheckInWizard = ({ onBack, onSubmit }: OrderCheckInWizardProps) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('basics');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderData, setOrderData] = useState<OrderData>({
    supplierCode: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    invoicePhotos: [],
    otherDocuments: [],
    goodsPhotos: []
  });
  
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Auto-save to localStorage
  useEffect(() => {
    const saved = localStorage.getItem('checkin-draft');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setOrderData(parsed);
      } catch (e) {
        console.warn('Failed to load saved draft');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('checkin-draft', JSON.stringify(orderData));
  }, [orderData]);

  const updateOrderData = (updates: Partial<OrderData>) => {
    setOrderData(prev => ({ ...prev, ...updates }));
  };

  const getCurrentStepIndex = () => steps.findIndex(step => step.key === currentStep);
  const progress = ((getCurrentStepIndex() + 1) / steps.length) * 100;

  const goToNextStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].key);
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].key);
    }
  };

  const canProceedFromCurrentStep = () => {
    switch (currentStep) {
      case 'basics':
        return orderData.supplierCode && orderData.deliveryDate;
      case 'invoice':
        return true; // Optional step
      case 'documents':
        return true; // Optional step
      case 'goods':
        return true; // Optional step
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      // Clear the draft when submitting
      localStorage.removeItem('checkin-draft');
      
      await onSubmit({
        supplierCode: orderData.supplierCode,
        deliveryDate: orderData.deliveryDate,
        documents: [], // Legacy field
        invoicePhotos: orderData.invoicePhotos,
        otherDocuments: orderData.otherDocuments,
        goodsPhotos: orderData.goodsPhotos
      });
      
      toast({
        title: "Order Created Successfully",
        description: "Your order check-in has been completed",
      });
    } catch (error: any) {
      toast({
        title: "Error Creating Order",
        description: error.message || "Failed to create order",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const currentStepData = steps.find(step => step.key === currentStep);

  return (
    <div className={`min-h-screen bg-background ${isMobile ? 'pb-safe' : ''}`}>
      {/* Mobile Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b">
        <div className="container mx-auto p-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "default"}
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {!isMobile && "Back"}
            </Button>
            <div className="flex-1">
              <h1 className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                Order Check-In
              </h1>
              <p className="text-muted-foreground text-sm">
                {currentStepData?.description}
              </p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {getCurrentStepIndex() + 1} of {steps.length}</span>
              <span>{currentStepData?.title}</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="container mx-auto p-4 space-y-6">
        <Card className="w-full">
          <CardContent className="p-6">
            {currentStep === 'basics' && (
              <SupplierQuickSelect
                supplierCode={orderData.supplierCode}
                deliveryDate={orderData.deliveryDate}
                onSupplierChange={(code) => updateOrderData({ supplierCode: code })}
                onDateChange={(date) => updateOrderData({ deliveryDate: date })}
              />
            )}

            {currentStep === 'invoice' && (
              <InvoicePhotoCapture
                photos={orderData.invoicePhotos}
                onPhotosChange={(photos) => updateOrderData({ invoicePhotos: photos })}
              />
            )}

            {currentStep === 'documents' && (
              <DocumentDropzone
                documents={orderData.otherDocuments}
                onDocumentsChange={(docs) => updateOrderData({ otherDocuments: docs })}
              />
            )}

            {currentStep === 'goods' && (
              <GoodsPhotoGrid
                photos={orderData.goodsPhotos}
                onPhotosChange={(photos) => updateOrderData({ goodsPhotos: photos })}
              />
            )}

            {currentStep === 'review' && (
              <OrderSummaryCard orderData={orderData} />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4">
          {getCurrentStepIndex() > 0 && (
            <Button
              variant="outline"
              onClick={goToPreviousStep}
              disabled={isProcessing}
              className="flex-1 sm:flex-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          
          <div className="flex-1" />
          
          {currentStep === 'review' ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceedFromCurrentStep() || isProcessing}
              className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Order
                </>
              )}
            </Button>
          ) : (
            <>
              {['invoice', 'documents', 'goods'].includes(currentStep) && (
                <Button
                  variant="outline"
                  onClick={goToNextStep}
                  disabled={isProcessing}
                  className="flex-1 sm:flex-none"
                >
                  Skip this step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              <Button
                onClick={goToNextStep}
                disabled={!canProceedFromCurrentStep() || isProcessing}
                className="flex-1 sm:flex-none"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};